import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ContactSubmissions.css';

const API_BASE_URL = '/api';

// Icon Components
const ViewIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M3 13C6.6 5 17.4 5 21 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
    <path d="M12 17C10.3431 17 9 15.6569 9 14C9 12.3431 10.3431 11 12 11C13.6569 11 15 12.3431 15 14C15 15.6569 13.6569 17 12 17Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CloseIcon = () => (
  <svg width="20px" height="20px" strokeWidth="2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

function ContactSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Statistics states
  const [stats, setStats] = useState({
    total: 0,
    thisMonth: 0,
    lastSevenDays: 0,
    statusBreakdown: { new: 0, in_progress: 0, resolved: 0, closed: 0 }
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form states
  const [replyText, setReplyText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Notification states
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, []);

  useEffect(() => {
    filterSubmissions();
    calculateFilteredStats();
  }, [submissions, filterStatus, searchTerm, dateFrom, dateTo]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/contact/submissions?limit=1000`, {
        withCredentials: true
      });

      if (response.data.success) {
        setSubmissions(response.data.data.submissions);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showNotification('Failed to load submissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/contact/contact-stats`, {
        withCredentials: true
      });

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];

    // Filter only contact_us type
    filtered = filtered.filter(sub => sub.type === 'contact_us');

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(sub => sub.status === filterStatus);
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        return subDate >= fromDate;
      });
    }

    if (dateTo) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        return subDate <= toDate;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.name.toLowerCase().includes(search) ||
        sub.email.toLowerCase().includes(search) ||
        (sub.subject && sub.subject.toLowerCase().includes(search))
      );
    }

    setFilteredSubmissions(filtered);
    setCurrentPage(1);
  };

  const calculateFilteredStats = () => {
    let filtered = [...submissions];

    // Filter only contact_us type
    filtered = filtered.filter(sub => sub.type === 'contact_us');

    // Apply date range filter (same as table filter)
    if (dateFrom) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        return subDate >= fromDate;
      });
    }

    if (dateTo) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        return subDate <= toDate;
      });
    }

    // Calculate statistics from filtered data
    const total = filtered.length;

    // This month count
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth = filtered.filter(item => new Date(item.created_at) >= startOfMonth).length;

    // Last 7 days count
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastSevenDays = filtered.filter(item => new Date(item.created_at) >= sevenDaysAgo).length;

    // Status distribution
    const statusBreakdown = { new: 0, in_progress: 0, resolved: 0, closed: 0 };
    filtered.forEach(item => {
      if (statusBreakdown.hasOwnProperty(item.status)) {
        statusBreakdown[item.status]++;
      }
    });

    setStats({
      total,
      thisMonth,
      lastSevenDays,
      statusBreakdown
    });
  };

  const handleViewSubmission = async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/contact/submissions/${id}`, {
        withCredentials: true
      });

      if (response.data.success) {
        setSelectedSubmission(response.data.data);
        setStatusUpdate(response.data.data.submission.status);
        setReplyText('');
        setNoteText('');
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      showNotification('Failed to load submission details', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedSubmission(null);
    setReplyText('');
    setNoteText('');
    setStatusUpdate('');
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate || statusUpdate === selectedSubmission.submission.status) return;

    try {
      setUpdatingStatus(true);
      const response = await axios.put(
        `${API_BASE_URL}/admin/contact/submissions/${selectedSubmission.submission.id}/status`,
        { status: statusUpdate },
        { withCredentials: true }
      );

      if (response.data.success) {
        showNotification('Status updated successfully');
        await fetchSubmissions();
        await handleViewSubmission(selectedSubmission.submission.id);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification('Failed to update status', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      showNotification('Please enter a reply message', 'error');
      return;
    }

    try {
      setSendingReply(true);
      const response = await axios.post(
        `${API_BASE_URL}/admin/contact/submissions/${selectedSubmission.submission.id}/reply`,
        { replyText },
        { withCredentials: true }
      );

      if (response.data.success) {
        showNotification(response.data.simulated
          ? 'Reply simulated (SMTP not configured). Check console for email content.'
          : 'Reply sent successfully!');
        setReplyText('');
        await handleViewSubmission(selectedSubmission.submission.id);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      showNotification('Failed to send reply', 'error');
    } finally {
      setSendingReply(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      showNotification('Please enter a note', 'error');
      return;
    }

    try {
      setAddingNote(true);
      const response = await axios.post(
        `${API_BASE_URL}/admin/contact/submissions/${selectedSubmission.submission.id}/notes`,
        { note: noteText },
        { withCredentials: true }
      );

      if (response.data.success) {
        showNotification('Note added successfully');
        setNoteText('');
        await handleViewSubmission(selectedSubmission.submission.id);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showNotification('Failed to add note', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Pending', class: 'status-pending' },
      new: { label: 'New', class: 'status-new' },
      in_progress: { label: 'In Progress', class: 'status-progress' },
      resolved: { label: 'Resolved', class: 'status-resolved' },
      closed: { label: 'Closed', class: 'status-closed' }
    };
    const badge = badges[status] || badges.pending;
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubmissions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  return (
    <div className="contact-submissions-container">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="contact-submissions-header">
        <div className="header-left">
          <h1 className="page-title">Contact Submissions</h1>
          <p className="page-description">Manage and respond to contact form submissions</p>
        </div>
        <div className="header-right">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <input
            type="date"
            className="date-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From Date"
          />
          <input
            type="date"
            className="date-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To Date"
          />
          <input
            type="text"
            placeholder="Search by name, email..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Submissions</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">This Month</div>
          <div className="stat-value">{stats.thisMonth}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Last 7 Days</div>
          <div className="stat-value">{stats.lastSevenDays}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value">{stats.statusBreakdown.new + stats.statusBreakdown.in_progress}</div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="rating-distribution">
        <h3 className="section-title">Status Distribution</h3>
        <div className="distribution-bars">
          {[
            { status: 'new', label: 'New', color: '#dc3545' },
            { status: 'in_progress', label: 'In Progress', color: '#0d6efd' },
            { status: 'resolved', label: 'Resolved', color: '#28a745' },
            { status: 'closed', label: 'Closed', color: '#6c757d' }
          ].map(item => {
            const count = stats.statusBreakdown[item.status] || 0;
            const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={item.status} className="distribution-row">
                <div className="rating-label" style={{ minWidth: '120px' }}>
                  {item.label}
                </div>
                <div className="bar-container">
                  <div
                    className="bar-fill"
                    style={{
                      width: `${percentage}%`,
                      background: item.color
                    }}
                  ></div>
                </div>
                <div className="rating-stats">
                  <span className="count">{count}</span>
                  <span className="percentage">({percentage.toFixed(0)}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading submissions...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="contact-submissions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-state">
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  currentItems.map(sub => (
                    <tr key={sub.id}>
                      <td className="id-cell">#{sub.id}</td>
                      <td>{sub.name}</td>
                      <td className="email-cell">{sub.email}</td>
                      <td>{sub.subject || '-'}</td>
                      <td>{getStatusBadge(sub.status)}</td>
                      <td>{formatDate(sub.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleViewSubmission(sub.id)}
                            title="View Details"
                          >
                            <ViewIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      {showViewModal && selectedSubmission && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Contact Submission #{selectedSubmission.submission.id}</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <CloseIcon />
              </button>
            </div>
            <div className="modal-body">
              {/* Sender Information */}
              <div className="detail-section">
                <h3 className="section-title">Sender Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{selectedSubmission.submission.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{selectedSubmission.submission.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{selectedSubmission.submission.phone || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Subject:</label>
                    <span>{selectedSubmission.submission.subject || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Date:</label>
                    <span>{formatDateTime(selectedSubmission.submission.created_at)}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span>{getStatusBadge(selectedSubmission.submission.status)}</span>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="detail-section">
                <h3 className="section-title">Message</h3>
                <div className="message-box">
                  {selectedSubmission.submission.message}
                </div>
              </div>

              {/* Status Management */}
              <div className="detail-section">
                <h3 className="section-title">Status Management</h3>
                <div className="status-form">
                  <select
                    value={statusUpdate}
                    onChange={(e) => setStatusUpdate(e.target.value)}
                    className="form-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <button
                    onClick={handleStatusUpdate}
                    disabled={updatingStatus || statusUpdate === selectedSubmission.submission.status}
                    className="primary-btn"
                  >
                    {updatingStatus ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </div>

              {/* Email Reply */}
              <div className="detail-section">
                <h3 className="section-title">Email Reply</h3>
                <textarea
                  rows="5"
                  placeholder="Type your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="form-textarea"
                />
                <button
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="primary-btn success-btn"
                >
                  {sendingReply ? 'Sending...' : 'Send Reply'}
                </button>
              </div>

              {/* Internal Notes */}
              <div className="detail-section">
                <h3 className="section-title">Internal Notes</h3>
                <textarea
                  rows="3"
                  placeholder="Add internal note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="form-textarea"
                />
                <button
                  onClick={handleAddNote}
                  disabled={addingNote || !noteText.trim()}
                  className="primary-btn"
                >
                  {addingNote ? 'Adding...' : 'Add Note'}
                </button>

                {selectedSubmission.notes.length > 0 && (
                  <div className="notes-list">
                    <h4>Previous Notes:</h4>
                    {selectedSubmission.notes.map(note => (
                      <div key={note.id} className="note-item">
                        <div className="note-meta">
                          {formatDateTime(note.created_at)} - {note.created_by}
                        </div>
                        <div className="note-text">{note.note}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity Log */}
              <div className="detail-section">
                <h3 className="section-title">Activity Log</h3>
                {selectedSubmission.activity_log.length === 0 ? (
                  <p className="empty-text">No activity yet</p>
                ) : (
                  <div className="activity-list">
                    {selectedSubmission.activity_log.map(log => (
                      <div key={log.id} className="activity-item">
                        <div className="activity-meta">
                          {formatDateTime(log.created_at)}
                          {log.created_by && ` - ${log.created_by}`}
                        </div>
                        <div className="activity-text">
                          <strong>{log.action.replace(/_/g, ' ')}</strong>
                          {log.details && ` - ${log.details}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactSubmissions;
